import { useState } from "react";

import { formatBytes } from "./composerAttachments";
import { MediaLightbox } from "./MediaLightbox";
import styles from "./ThreadMedia.module.css";
import { Icon } from "../icons";
import { cx } from "../lib/cx";

// A picture or a clip in the chat thread, and the pop-out behind it.
//
// The thread has two producers of bytes and they render the same way. A file
// on a message — a user's upload, or one an agent published — hangs under the
// body (`chat.md` §Attachments); an image a tool result carried sits on that
// call's card (§Tool calls that return an image). Different tables, different
// content routes, different reasons for existing, but a reader clicking a
// picture wants the same thing from both, so the click belongs in one place
// rather than written out twice.
//
// Only ever a type the browser renders itself; the callers filter before they
// get here (`attachments.md` §Decisions 6). An image shows through an `<img>`
// and nothing else, which is what lets SVG in — secure static mode runs none
// of the script an SVG may carry. An HTML file has no such mode and stays a
// download chip.

/**
 * How big the thumbnail may get. The two values are deliberate and different —
 * see the stylesheet — so this names the placement rather than a number.
 */
export type ThreadMediaBound = "card" | "message";

export type ThreadMediaProps = {
  /** What the media is, for a reader who cannot see it. */
  readonly alt: string;
  readonly bound: ThreadMediaBound;
  /** The stored size, shown beside the name in the pop-out. */
  readonly bytes: number;
  /**
   * What a download is saved as. Separate from {@link name} because a tool's
   * screenshot has no filename of its own — the card titles it after the tool,
   * which is not something to write to a disk.
   */
  readonly filename: string;
  readonly kind: "image" | "video";
  /** The name the pop-out titles itself with. */
  readonly name: string;
  /**
   * Put this preview away, if the surface offers that. A message's attachments
   * each collapse to their own chip; a tool card's images collapse with the
   * card itself, so they get the download and no chevron (chat.md §Corner
   * controls).
   */
  readonly onCollapse?: () => void;
  /**
   * Called when a clip turns out not to be playable here, so the caller can
   * fall back to the download chip. A `.mov` is a container and not a codec:
   * it may hold ProRes, which no browser decodes. A dead black rectangle is a
   * worse answer than the chip the file would have had before.
   */
  readonly onUnplayable?: () => void;
  /** The authenticated content route the bytes come from. */
  readonly src: string;
  /** Hover text. Defaults to {@link name}. */
  readonly title?: string;
};

/**
 * A media fragment asking for the very first frame.
 *
 * A `<video>` with no poster paints nothing until it has decoded something, so
 * an unplayed clip in the thread is a black rectangle with a play badge over
 * it — indistinguishable from a broken one. Seeking a millisecond in makes the
 * browser fetch and paint that frame as the still. It is also why the content
 * route serves ranges (`api.md` §Chat attachments): without them the seek
 * drags the whole file down to show one frame.
 */
const FIRST_FRAME = "#t=0.001";

/**
 * A bounded thumbnail that opens over the dimmed page when clicked.
 *
 * The open state lives here rather than on the row above, which is what lets
 * a message interleave thumbnails with download chips in one wrapping strip:
 * there is no strip component to own it. Only one image can be open at a time
 * regardless — the pop-out is modal, and it has to be closed before another
 * thumbnail can be reached.
 */
export const ThreadMedia = ({
  alt,
  bound,
  bytes,
  filename,
  kind,
  name,
  onCollapse,
  onUnplayable,
  src,
  title,
}: ThreadMediaProps) => {
  const [enlarged, setEnlarged] = useState(false);
  return (
    // The wrapper exists so the corner controls can sit over the thumbnail
    // without being *inside* it: the thumbnail is a button, and neither a
    // button nor a link nests inside one and survives.
    <span className={cx(styles.wrap, styles[bound])}>
      <button
        className={styles.thumb}
        onClick={() => {
          setEnlarged(true);
        }}
        title={title ?? name}
        type="button"
      >
        {kind === "video" ? (
          <span className={styles.frame}>
            <video
              aria-label={alt}
              className={styles.media}
              muted
              onError={onUnplayable}
              // Metadata and the one frame the fragment asks for. A thread is
              // not the place to start pulling megabytes nobody has said they
              // want to watch.
              preload="metadata"
              src={src + FIRST_FRAME}
            />
            {/* Over the frame rather than beside it: what makes a still read
                as a clip is the badge on top of it, and the whole thumbnail
                is already one button. */}
            <span aria-hidden className={styles.play}>
              <Icon.Play size={16} />
            </span>
          </span>
        ) : (
          <img alt={alt} className={styles.media} loading="lazy" src={src} />
        )}
      </button>
      <span className={styles.controls}>
        {/* An anchor and not a button: both content routes answer with
            `Content-Disposition: attachment`, so the download is the server's
            and this only has to point at it (chat.md §Corner controls). */}
        <a
          className={styles.control}
          download={filename}
          href={src}
          title={`Download ${filename}`}
        >
          <Icon.ArrowDown size={12} />
        </a>
        {onCollapse ? (
          // The same chevron the tool card turns, meaning the same thing:
          // there is content here and this is the control over it. Pointing
          // down while open, so the collapsed chip's own chevron — pointing
          // right — reads as the other half of one toggle.
          <button
            className={styles.control}
            onClick={onCollapse}
            title="Collapse"
            type="button"
          >
            <Icon.ChevDown size={12} />
          </button>
        ) : null}
      </span>
      {enlarged ? (
        <MediaLightbox
          caption={formatBytes(bytes)}
          filename={filename}
          kind={kind}
          name={name}
          onClose={() => {
            setEnlarged(false);
          }}
          src={src}
        />
      ) : null}
    </span>
  );
};
